import { Lightning, Registry } from '@lightningjs/sdk';
import { EpgChannel, LiveEventStream, Video } from 'types/api/media';
import { PageId } from 'types/pageId';
import {
  ErrorType,
  PlaybackEvent,
  SeekType,
  ViewContext,
} from 'types/analytics';
import {
  reportBackgrounded,
  reportBitrate,
  reportContentMetadata,
  reportForegrounded,
  reportHeartbeat,
  reportPlayback,
  reportPlaybackError,
  reportPlayerLoading,
  reportStreamLoaded,
} from 'services/analytics/reportingServiceVideo';
import { ListItemContext } from 'types/events';
import AbstractPlayerEventConsumer from 'components/pages/playback/player-event-consumers/AbstractPlayerEventConsumer';
import LightningPlayerEventConsumer from 'components/pages/playback/player-event-consumers/LightningPlayerEventConsumer';
import BrightcovePlayerEventConsumer from 'components/pages/playback/player-event-consumers/BrightcovePlayerEventConsumer';
import AbstractPlayer from 'components/pages/playback/player-components/AbstractPlayer';
import { secondsToMilliseconds } from 'support/timeUtils';

export interface AbstractPlayerEventHandlerTemplateSpec
  extends Lightning.Component.TemplateSpec {
  LightningPlayerEventConsumer: typeof LightningPlayerEventConsumer;
  BrightcovePlayerEventConsumer: typeof BrightcovePlayerEventConsumer;
  seekType: SeekType | null;
  playerType: 'lightning' | 'brightcove';
}

const LONG_HEARTBEAT_INTERVAL_SEC = 10;

export default abstract class AbstractPlayerEventHandler
  extends Lightning.Component<AbstractPlayerEventHandlerTemplateSpec>
  implements
    Lightning.Component
      .ImplementTemplateSpec<AbstractPlayerEventHandlerTemplateSpec>
{
  playerType: 'lightning' | 'brightcove' = 'lightning';

  private _LightningPlayerEventConsumer = this.getByRef(
    'LightningPlayerEventConsumer',
  )!;
  private _BrightcovePlayerEventConsumer = this.getByRef(
    'BrightcovePlayerEventConsumer',
  )!;

  protected _Player: AbstractPlayer | null = null;

  protected _fromPageId: PageId | null = null;
  protected _isInitialized = false;
  protected _isBuffering = false;
  protected _isAdPlaying = false;
  protected _hasStarted = false;
  protected _longAnalyticsHeartbeatIntervalId: number | null = null;
  protected _canCreditEvent = true;
  protected _hasReachedCreditsOnce = false;
  protected _updatingAccountBookmark = false;
  protected _contentAddedToAccount = false;

  seekType: SeekType | null = null;

  static override _template(): Lightning.Component.Template<AbstractPlayerEventHandlerTemplateSpec> {
    return {
      LightningPlayerEventConsumer: {
        type: LightningPlayerEventConsumer,
        signals: AbstractPlayerEventConsumer.playerSignals,
      },
      BrightcovePlayerEventConsumer: {
        type: BrightcovePlayerEventConsumer,
        signals: AbstractPlayerEventConsumer.playerSignals,
      },
    };
  }

  getConsumer(): Lightning.Component {
    switch (this.playerType) {
      case 'brightcove':
        return this._BrightcovePlayerEventConsumer;
      case 'lightning':
      default:
        return this._LightningPlayerEventConsumer;
    }
  }

  initialize(
    player: AbstractPlayer,
    mediaContent: Video | LiveEventStream | EpgChannel,
    fromPageId: PageId | null,
    viewContext: ViewContext | null,
    listItemContext: ListItemContext | undefined,
  ) {
    this._Player = player;

    this.resetAnalytics();

    this.initializePlayerContent(mediaContent, viewContext);

    this._isInitialized = true;
    this._hasStarted = false;
    this.seekType = null;
    this._canCreditEvent = true;
    this._hasReachedCreditsOnce = false;
    this._contentAddedToAccount = false;
    this._updatingAccountBookmark = false;
    this._fromPageId = fromPageId;

    reportContentMetadata({
      mediaContent,
      fromPageId,
      viewContext,
      listItemContext,
    });

    window.backgroundingService.addBackgroundingAction(
      'reportBackgrounded',
      reportBackgrounded,
    );
    window.backgroundingService.addForegroundingAction(
      'reportForegrounded',
      reportForegrounded,
    );

    this._Player.beforeLoad = () => {
      reportPlayerLoading();
    };

    this._Player.onStreamAdaption = (bitrate: number) => {
      reportBitrate(bitrate);
      this.signal('$streamAdaption');
    };

    this._Player.onStreamLoaded = (url: string) => {
      reportStreamLoaded(url);
      this.signal('$streamLoaded');
    };

    this._Player.onPlaybackError = (isFatal: boolean, message: string) => {
      reportPlaybackError(isFatal, ErrorType.STREAM_ERROR, message);
      if (isFatal) this.signal('$fatalError');
    };

    this._Player.onStreamError = (isFatal: boolean, message: string) => {
      reportPlaybackError(isFatal, ErrorType.STREAM_ERROR, message);
      if (isFatal) this.signal('$fatalError');
    };
  }

  /* =============================
   * Player
   * ============================= */

  protected abstract initializePlayerContent(
    mediaContent: Video | LiveEventStream | EpgChannel,
    viewContext: ViewContext | null,
  ): void;

  handlePlayerEnd(args: { hasReachedEnd: boolean }) {
    const { hasReachedEnd } = args;

    if (!this._isInitialized) return;

    this._isInitialized = false;
    this._hasStarted = false;

    const relativeTime = this._Player!.currentContentTime();
    const rawTime = this._Player!.currentRawTime();

    window.backgroundingService.removeBackgroundingAction('reportBackgrounded');
    window.backgroundingService.removeForegroundingAction('reportForegrounded');

    this.resetAnalytics(hasReachedEnd);

    reportPlayback(
      PlaybackEvent.END,
      { relativeTime, rawTime },
      {
        hasEnded: hasReachedEnd,
        isAdPlaying: this._isAdPlaying,
      },
    );
  }

  protected handlePlayerStart(rawTime: number) {
    if (!this._Player || this._hasStarted) {
      return;
    }

    this.reportPlayerStart(rawTime);
    this.startAnalytics();
    this._hasStarted = true;
  }

  /* =============================
   * Analytics
   * ============================= */

  protected startAnalytics() {
    this.startLongAnalyticsHeartbeat();
  }

  protected resetAnalytics(isComplete?: boolean) {
    this.stopLongAnalyticsHeartbeat();
  }

  private startLongAnalyticsHeartbeat() {
    this._longAnalyticsHeartbeatIntervalId = Registry.setInterval(() => {
      const currentTime = this._Player!.getRelativeTime();
      reportHeartbeat(currentTime);
    }, secondsToMilliseconds(LONG_HEARTBEAT_INTERVAL_SEC));
  }

  private stopLongAnalyticsHeartbeat() {
    if (this._longAnalyticsHeartbeatIntervalId !== null) {
      Registry.clearInterval(this._longAnalyticsHeartbeatIntervalId);
      this._longAnalyticsHeartbeatIntervalId = null;
    }
  }

  protected reportPlayerStart(rawTime: number) {
    const relativeTime = this.getStartRelativeTime();
    const relativeStartTime = this._Player!.startTime ?? relativeTime;
    const rawStartTime =
      this._Player!.getStreamTimeForContentTime(relativeStartTime) ?? rawTime;

    reportPlayback(PlaybackEvent.START, {
      relativeTime: relativeStartTime,
      rawTime: rawStartTime,
    });
  }

  protected getStartRelativeTime(): number {
    return this._Player!.getRelativeTime() ?? 0;
  }

  /* =============================
   * Events

    Events listed: https://github.com/Metrological/metrological-sdk/blob/master/docs/plugins/videoplayer.md
    "$videoPlayerPause" is missing from this list, but seems to be implemented

    Note: the rawTime argument includes time from ads. For example if a video has a 40 second pre-roll then the
    rawTime will be 40 when the actual content starts playing
  
   * ============================= */

  $videoPlayerAbort(rawTime: number) {
    const relativeTime = this._Player!.currentAdTime();

    this.resetAnalytics();
    reportPlayback(
      PlaybackEvent.END,
      { relativeTime, rawTime },
      {
        hasEnded: false,
        isAdPlaying: this._isAdPlaying,
      },
    );
  }

  $videoPlayerCanPlay(rawTime: number, willResume?: boolean) {
    const relativeTime = this._Player!.getRelativeTime();
    const isPlaying = this._Player?.isPlaying();

    if (this._isBuffering) {
      reportPlayback(
        PlaybackEvent.BUFFER_END,
        { relativeTime, rawTime },
        isPlaying || willResume,
      );
      this._isBuffering = false;

      this.signal('$bufferEnd');
    }
    this.handlePlayerStart(rawTime);
  }

  $videoPlayerCanPlayThrough(rawTime: number, willResume?: boolean) {
    const relativeTime = this._Player!.getRelativeTime();
    const isPlaying = this._Player?.isPlaying();

    if (this._isBuffering) {
      reportPlayback(
        PlaybackEvent.BUFFER_END,
        { relativeTime, rawTime },
        isPlaying || willResume,
      );
      this._isBuffering = false;

      this.signal('$bufferEnd');
    }
  }

  $videoPlayerWaiting(rawTime: number) {
    const relativeTime = this._Player!.getRelativeTime();

    reportPlayback(PlaybackEvent.BUFFER_START, { relativeTime, rawTime });
    this._isBuffering = true;

    this.signal('$bufferStart');
  }

  $videoPlayerEnded(rawTime: number) {
    const relativeTime = this._Player!.currentAdTime();

    // PlaybackEvent.COMPLETE is called on content completion, this assumes there is no post-roll
    // If a post-roll is added this logic will need to be changed (primarily for Nielsen)
    reportPlayback(PlaybackEvent.COMPLETE, { relativeTime, rawTime });

    this.resetAnalytics(true);

    this.signal('$videoPlayerEnded');
  }

  $videoPlayerPlay(rawTime: number) {
    this.signal('$videoPlayerPlay');
    if (this._hasStarted) {
      const relativeTime = this._Player!.getRelativeTime();
      if (!relativeTime) return;
      reportPlayback(PlaybackEvent.RESUME, { relativeTime, rawTime });
    }
  }

  $videoPlayerPause(rawTime: number) {
    const relativeTime = this._Player!.getRelativeTime();

    this.signal('$videoPlayerPause');

    reportPlayback(PlaybackEvent.PAUSE, { relativeTime, rawTime });
  }

  $videoPlayerSeeked(rawTime: number) {
    const relativeTime = this._Player!.currentContentTime(); // We shouldn't be able to seek during ads

    reportPlayback(PlaybackEvent.SEEK_END, { relativeTime, rawTime });

    this.signal('$videoPlayerSeeked');
  }

  $videoPlayerSeeking(rawTime: number) {
    if (!this._hasStarted) return;
    // this is the time we're seeking to
    const relativeTime = this._Player!.currentContentTime(); // We shouldn't be able to seek during ads

    reportPlayback(
      PlaybackEvent.SEEK_START,
      { relativeTime, rawTime },
      this.seekType ?? undefined,
    );

    this.signal('$videoPlayerSeeking', rawTime, relativeTime);
  }

  $videoPlayerTimeUpdate(rawTime: number) {
    if (!this._Player) return;

    const relativeTime = this._Player.getRelativeTime();

    this.signal(
      '$videoPlayerTimeUpdate',
      relativeTime,
      this._hasReachedCreditsOnce,
    );
  }

  $videoManagerSeekPause() {
    const relativeTime = this._Player!.currentContentTime();
    const rawTime = this._Player!.currentRawTime();

    reportPlayback(PlaybackEvent.SEEK_PAUSE, { relativeTime, rawTime });
  }

  $videoManagerBeforeSeek() {
    const relativeTime = this._Player!.currentContentTime(); // We shouldn't be able to seek during ads
    const rawTime = this._Player!.currentRawTime();

    reportPlayback(PlaybackEvent.BEFORE_SEEK, { relativeTime, rawTime });
  }

  $videoPlayerUnloading() {
    this.signal('$videoPlayerUnloading');
  }

  $videoManagerSkipAdPod(type: string) {
    // no-op
  }

  $videoManagerCleanupAdPod(type: string) {
    // no-op
  }

  $videoManagerAdBreakEnded(type: string) {
    // no-op
  }

  $videoManagerAdBreakStarted() {
    // no-op
  }

  $videoManagerStarted(event: unknown) {
    // no-op
  }

  $videoManagerComplete() {
    // no-op
  }
}
